import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@clerk/react";
import {
  useAssistantAnalyzeNow,
  useGetAssistantAnalyzeJob,
  useCancelAssistantAnalyzeJob,
  getGetAssistantAnalyzeJobQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Wand2, Loader2, CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const JOB_STORAGE_KEY = "onenext.analyzeCard.jobId";

function readPersistedJob(userId?: string | null): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(JOB_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: string; jobId?: string };
    if (!parsed.jobId) return null;
    if (userId && parsed.userId && parsed.userId !== userId) return null;
    return parsed.jobId;
  } catch {
    return null;
  }
}

function writePersistedJob(userId: string | null | undefined, jobId: string | null) {
  if (typeof window === "undefined") return;
  if (!jobId) {
    window.localStorage.removeItem(JOB_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(JOB_STORAGE_KEY, JSON.stringify({ userId: userId ?? null, jobId }));
}

type RangeId = "5d" | "10d" | "30d" | "3m" | "6m" | "1y" | "all";

type Range = {
  id: RangeId;
  sinceDays: number | null;
  depth: "3m" | "12m" | "all";
};

const RANGES: Range[] = [
  { id: "5d",  sinceDays: 5,    depth: "3m"  },
  { id: "10d", sinceDays: 10,   depth: "3m"  },
  { id: "30d", sinceDays: 30,   depth: "3m"  },
  { id: "3m",  sinceDays: 90,   depth: "12m" },
  { id: "6m",  sinceDays: 180,  depth: "all" },
  { id: "1y",  sinceDays: 365,  depth: "all" },
  { id: "all", sinceDays: null, depth: "all" },
];

type Outcome = "pending" | "done" | "failed" | "cancelled";

export default function AnalyzeNowCard() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useUser();

  const [selected, setSelected] = useState<RangeId>("30d");
  const [jobOutcome, setJobOutcome] = useState<Outcome>("pending");

  const [jobId, setJobIdState] = useState<string | null>(() =>
    readPersistedJob(user?.id),
  );

  const setJobId = (id: string | null) => {
    writePersistedJob(user?.id, id);
    setJobIdState(id);
  };

  const analyzeNow = useAssistantAnalyzeNow();
  const cancelJob = useCancelAssistantAnalyzeJob();

  // How many mails were processed at the moment of cancellation/failure
  const [doneAtStop, setDoneAtStop] = useState(0);

  const job = useGetAssistantAnalyzeJob(jobId ?? "", {
    query: {
      queryKey: getGetAssistantAnalyzeJobQueryKey(jobId ?? ""),
      enabled: !!jobId && jobOutcome === "pending",
      refetchInterval: (query) => {
        const data = query.state.data as { status?: string } | undefined;
        return data?.status === "running" || data?.status === "paused"
          ? 1500
          : false;
      },
      // If the server restarted the job no longer exists in memory (404).
      // Treat it as cancelled so the UI doesn't stay stuck in "running".
      retry: false,
    },
  });

  // Handle terminal status from polling
  useEffect(() => {
    if (!job.data) return;
    if (job.data.status === "done") {
      setDoneAtStop((job.data.inboxDone ?? 0) + (job.data.mailsDone ?? 0));
      setJobOutcome("done");
      writePersistedJob(user?.id, null);
    } else if (job.data.status === "failed") {
      setDoneAtStop((job.data.inboxDone ?? 0) + (job.data.mailsDone ?? 0));
      setJobOutcome("failed");
      writePersistedJob(user?.id, null);
    } else if (job.data.status === "cancelled") {
      setDoneAtStop((job.data.inboxDone ?? 0) + (job.data.mailsDone ?? 0));
      setJobOutcome("cancelled");
      writePersistedJob(user?.id, null);
    }
  }, [job.data, user?.id]);

  // If polling itself errors (job not found / server restart) → silently reset
  // to the initial ready state so the user can relaunch without confusion.
  useEffect(() => {
    if (job.isError && jobOutcome === "pending") {
      setJobId(null);
      setJobOutcome("pending");
    }
  }, [job.isError, jobOutcome]);

  const handleStart = () => {
    const range = RANGES.find((r) => r.id === selected);
    if (!range) return;
    setJobOutcome("pending");
    setDoneAtStop(0);
    analyzeNow.mutate(
      {
        data: {
          sinceDays: range.sinceDays,
          unlimited: true,
          depth: range.depth,
          maxMails: null,
        },
      },
      {
        onSuccess: (data) => {
          const id = (data as { jobId?: string }).jobId;
          if (id) setJobId(id);
        },
        onError: (err) => {
          toast({
            title: t("analyze_card.error_title"),
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          });
        },
      },
    );
  };

  const handleCancel = () => {
    if (!jobId) return;
    // Snapshot the current count before cancelling
    const countNow = done;
    cancelJob.mutate({ jobId }, {
      onSuccess: () => {
        // Immediate UI update — don't wait for the next poll
        setDoneAtStop(countNow);
        setJobOutcome("cancelled");
        writePersistedJob(user?.id, null);
      },
      onError: (err) => {
        toast({
          title: t("analyze_card.cancel_error_title"),
          description: err instanceof Error ? err.message : t("analyze_card.cancel_error_desc"),
          variant: "destructive",
        });
      },
    });
  };

  const handleReset = () => {
    setJobId(null);
    setJobOutcome("pending");
  };

  const options: { id: RangeId; label: string }[] = [
    { id: "5d",  label: t("analyze_card.option_5d")  },
    { id: "10d", label: t("analyze_card.option_10d") },
    { id: "30d", label: t("analyze_card.option_30d") },
    { id: "3m",  label: t("analyze_card.option_3m")  },
    { id: "6m",  label: t("analyze_card.option_6m")  },
    { id: "1y",  label: t("analyze_card.option_1y")  },
    { id: "all", label: t("analyze_card.option_all") },
  ];

  const isRunning = !!jobId && jobOutcome === "pending";
  const isDone = jobOutcome === "done";
  const isFailed = jobOutcome === "failed";
  const isCancelled = jobOutcome === "cancelled";

  const phase = job.data?.phase ?? "analyzing";
  const isSyncing = phase === "syncing" && isRunning;

  const syncMailsTotal = job.data?.syncMailsTotal ?? 0;
  const syncMailsDone  = job.data?.syncMailsDone ?? 0;
  const syncTotal      = job.data?.syncTotal ?? 0;
  const syncDone       = job.data?.syncDone ?? 0;
  const syncFolder     = job.data?.syncCurrentFolder ?? null;
  const syncPct =
    syncMailsTotal > 0
      ? Math.min(100, Math.round((syncMailsDone / syncMailsTotal) * 100))
      : syncTotal > 0
        ? Math.round((syncDone / syncTotal) * 100)
        : 0;

  const total   = (job.data?.inboxTotal ?? 0) + (job.data?.mailsTotal ?? 0);
  const done    = (job.data?.inboxDone  ?? 0) + (job.data?.mailsDone  ?? 0);
  const failed  = (job.data?.inboxFailed ?? 0) + (job.data?.mailsFailed ?? 0);
  const pct     = total > 0 ? Math.round((done / total) * 100) : 0;

  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-primary" />
          {t("analyze_card.title")}
        </CardTitle>
        <CardDescription>{t("analyze_card.desc")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isRunning && !isDone && !isFailed && !isCancelled && (
          <>
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setSelected(opt.id)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    selected === opt.id
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "bg-card text-foreground border-border hover:border-primary/50 hover:bg-primary/5"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button
              onClick={handleStart}
              disabled={analyzeNow.isPending}
              className="w-full"
            >
              {analyzeNow.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("analyze_card.running")}
                </>
              ) : (
                t("analyze_card.start_btn")
              )}
            </Button>
          </>
        )}

        {isRunning && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
              <span>
                {isSyncing
                  ? t("analyze_card.syncing_label")
                  : t("analyze_card.analyzing_label")}
              </span>
            </div>

            {isSyncing ? (
              <div className="space-y-1.5">
                {syncMailsTotal > 0 ? (
                  <>
                    <Progress value={syncPct} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {syncMailsDone.toLocaleString(locale)} / {syncMailsTotal.toLocaleString(locale)} {t("analyze_card.sync_mails_unit")}
                      </span>
                      <span>{syncPct}%</span>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Indeterminate bar while we don't know the volume yet */}
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full animate-[indeterminate_1.4s_ease-in-out_infinite]" style={{ width: "40%" }} />
                    </div>
                    <p className="text-xs text-muted-foreground">{t("analyze_card.sync_preparing")}</p>
                  </>
                )}
                {syncFolder && (
                  <p className="text-xs text-muted-foreground truncate">{syncFolder}</p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                {total > 0 ? (
                  <>
                    <Progress value={pct} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{done.toLocaleString(locale)} / {total.toLocaleString(locale)} {t("analyze_card.analyze_mails_unit")}</span>
                      <span>{pct}%</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full animate-[indeterminate_1.4s_ease-in-out_infinite]" style={{ width: "40%" }} />
                    </div>
                    <p className="text-xs text-muted-foreground">{t("analyze_card.analyze_preparing")}</p>
                  </>
                )}
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={cancelJob.isPending}
              onClick={handleCancel}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              {t("analyze_card.cancel_btn")}
            </Button>
          </div>
        )}

        {isDone && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-primary">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{t("analyze_card.done_label", { done, failed })}</span>
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={handleReset}>
              {t("analyze_card.rerun_btn")}
            </Button>
          </div>
        )}

        {(isFailed || isCancelled) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-destructive">
              {isFailed
                ? <AlertTriangle className="w-4 h-4 shrink-0" />
                : <XCircle className="w-4 h-4 shrink-0" />}
              <span>
                {isFailed ? t("analyze_card.failed_label") : t("analyze_card.cancelled_label")}
              </span>
            </div>
            {/* Reassure the user: results already analysed are saved */}
            {doneAtStop > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("analyze_card.partial_saved", { count: doneAtStop.toLocaleString(locale) })}
              </p>
            )}
            <Button variant="outline" size="sm" className="w-full" onClick={handleReset}>
              {t("analyze_card.retry_btn")}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
